// src/modules/orders/components/OrderStatusModal.tsx
import React from 'react';
import { Modal, Select, Input, Space, Typography, Alert, Tag } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Option } = Select;
const { Text } = Typography;

// Статусы заказов
const ORDER_STATUS_MAP = {
  pending: { color: 'orange', text: 'Ожидает' },
  confirmed: { color: 'blue', text: 'Подтвержден' },
  preparing: { color: 'yellow', text: 'Формируется' },
  delivering: { color: 'cyan', text: 'Доставляется' },
  completed: { color: 'green', text: 'Завершен' },
  cancelled: { color: 'red', text: 'Отменен' },
  abandoned: { color: 'gray', text: 'Брошенная корзина' },
};

// ✨ ПРАВИЛА ПЕРЕХОДОВ СТАТУСОВ (синхронизировано с backend)
const ALLOWED_STATUS_TRANSITIONS: Record<string, string[]> = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['preparing', 'cancelled'],
  preparing: ['delivering', 'cancelled'],
  delivering: ['preparing', 'completed', 'cancelled'], // ✨ Добавлен возврат в preparing
  completed: ['confirmed'], // Откат при ошибке
  cancelled: [], // Финальный статус
  abandoned: ['pending'], // Восстановление
};

interface OrderStatusModalProps {
  visible: boolean;
  loading: boolean;
  currentStatus: string; // ✨ ДОБАВЛЕНО: текущий статус заказа
  newStatus: string;
  comment: string;
  onStatusChange: (status: string) => void;
  onCommentChange: (comment: string) => void;
  onOk: () => void;
  onCancel: () => void;
}

export const OrderStatusModal: React.FC<OrderStatusModalProps> = ({
  visible,
  loading,
  currentStatus, // ✨ ДОБАВЛЕНО
  newStatus,
  comment,
  onStatusChange,
  onCommentChange,
  onOk,
  onCancel,
}) => {
  // ✨ Функция проверки возможности перехода
  const isTransitionAllowed = (fromStatus: string, toStatus: string): boolean => {
    if (fromStatus === toStatus) return true; // Тот же статус - разрешено
    const allowedStatuses = ALLOWED_STATUS_TRANSITIONS[fromStatus] || [];
    return allowedStatuses.includes(toStatus);
  };

  // ✨ Получение списка разрешенных статусов
  const getAllowedStatuses = (): string[] => {
    return ALLOWED_STATUS_TRANSITIONS[currentStatus] || [];
  };

  // ✨ Получение подсказки для недопустимого перехода
  const getDisabledReason = (toStatus: string): string => {
    if (currentStatus === toStatus) return '';
    
    const allowed = getAllowedStatuses();
    const allowedText = allowed
      .map(s => ORDER_STATUS_MAP[s as keyof typeof ORDER_STATUS_MAP]?.text)
      .join(', ');
    
    return `Из статуса "${ORDER_STATUS_MAP[currentStatus as keyof typeof ORDER_STATUS_MAP]?.text}" можно перейти только в: ${allowedText || 'нет доступных переходов'}`;
  };

  // ✨ Проверка валидности выбранного статуса
  const isSelectedStatusValid = isTransitionAllowed(currentStatus, newStatus);

  return (
    <Modal
      title="Изменить статус заказа"
      open={visible}
      onOk={onOk}
      onCancel={onCancel}
      confirmLoading={loading}
      okText="Сохранить"
      cancelText="Отмена"
      okButtonProps={{ disabled: !isSelectedStatusValid || !newStatus }} // ✨ Блокируем кнопку если переход недопустим
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* ✨ Информация о текущем статусе */}
        <div>
          <Text type="secondary">Текущий статус: </Text>
          <Tag color={ORDER_STATUS_MAP[currentStatus as keyof typeof ORDER_STATUS_MAP]?.color}>
            {ORDER_STATUS_MAP[currentStatus as keyof typeof ORDER_STATUS_MAP]?.text}
          </Tag>
        </div>

        {/* Выбор нового статуса */}
        <div>
          <Text>Новый статус:</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            value={newStatus}
            onChange={onStatusChange}
            placeholder="Выберите статус"
          >
            {Object.entries(ORDER_STATUS_MAP).map(([key, config]) => {
              const isAllowed = isTransitionAllowed(currentStatus, key);
              const isCurrent = currentStatus === key;
              
              return (
                <Option 
                  key={key} 
                  value={key}
                  disabled={!isAllowed} // ✨ Делаем недоступные статусы неактивными
                >
                  <Space>
                    {/* ✨ Иконка статуса доступности */}
                    {isCurrent ? (
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    ) : isAllowed ? (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#d9d9d9' }} />
                    )}
                    <span style={{ color: isAllowed || isCurrent ? 'inherit' : '#d9d9d9' }}>
                      {config.text}
                    </span>
                  </Space>
                </Option>
              );
            })}
          </Select>
        </div>

        {/* ✨ Предупреждение о недопустимом переходе */}
        {newStatus && !isSelectedStatusValid && (
          <Alert
            message="Недопустимый переход"
            description={getDisabledReason(newStatus)}
            type="error"
            showIcon
            icon={<CloseCircleOutlined />}
          />
        )}

        {/* ✨ Информация о разрешенных переходах */}
        {!newStatus && getAllowedStatuses().length > 0 && (
          <Alert
            message="Доступные переходы"
            description={
              <div>
                <Text>Из текущего статуса можно перейти в:</Text>
                <div style={{ marginTop: 8 }}>
                  {getAllowedStatuses().map(status => (
                    <Tag 
                      key={status} 
                      color={ORDER_STATUS_MAP[status as keyof typeof ORDER_STATUS_MAP]?.color}
                      style={{ marginBottom: 4 }}
                    >
                      {ORDER_STATUS_MAP[status as keyof typeof ORDER_STATUS_MAP]?.text}
                    </Tag>
                  ))}
                </div>
              </div>
            }
            type="info"
            showIcon
          />
        )}

        {/* ✨ Специальная подсказка для delivering → preparing */}
        {currentStatus === 'delivering' && newStatus === 'preparing' && (
          <Alert
            message="Возврат в подготовку"
            description="Товары вернутся из доставки в подготовку. Используйте эту опцию если заказ нужно пересобрать или курьер вернулся."
            type="warning"
            showIcon
          />
        )}

        {/* Комментарий */}
        <div>
          <Text>Комментарий (необязательно):</Text>
          <Input.TextArea
            style={{ marginTop: 8 }}
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            placeholder="Комментарий к изменению статуса"
            rows={3}
          />
        </div>
      </Space>
    </Modal>
  );
};